import React from 'react';
import { useHistory } from 'react-router-dom';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import { useTranslation } from 'react-i18next';
import { UseMutationOptions } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

type MutationOptions = UseMutationOptions<AxiosResponse<any>, unknown, any, unknown>;

type Args = {
    queryInvalidateFunction: () => void;
    ruleId?: string;
    mutationOptions: MutationOptions;
    tenantGroupId: string;
    tenantGroupName?: string;
};

const useDeleteTenantGroupOverrideErrorHandler = (
    { mutationOptions, ruleId, queryInvalidateFunction, tenantGroupId, tenantGroupName }: Args = {
        mutationOptions: {},
        queryInvalidateFunction: () => {},
        tenantGroupId: '',
        tenantGroupName: ''
    }
) => {
    const { t } = useTranslation();
    const history = useHistory();
    const { clearAll, addTechnicalErrorModal } = useReduxModal();
    const { onError } = mutationOptions;
    const content = <>{t('alreadyDeletedTenantGroupOverride')}</>;
    const errorHandler = React.useCallback(
        (error: any, variables?: any, context?: any) => {
            const handleNotFound = () => {
                addTechnicalErrorModal(
                    '',
                    t('Tenant Group Override Already Removed'),
                    () => {
                        clearAll();
                        queryInvalidateFunction();
                        history.push(`/tenants/tenant-group/${tenantGroupId}`);
                    },
                    {
                        status: 'error',
                        content
                    }
                );
            };

            const handleDefault = () => {
                addTechnicalErrorModal('', t('Unable to delete tenant group override', { rule: ruleId }));
            };

            const handleTenantGroupDeleted = () => {
                addTechnicalErrorModal(
                    '',
                    t('Tenant Group Unavailable'),
                    () => {
                        clearAll();
                        queryInvalidateFunction();
                        history.push('/tenants');
                    },
                    {
                        content: t('tenantGroupDeletedMsg', { tenantGroupName }),
                        cancelText: t('Return to Manage Tenants'),
                        status: 'error'
                    }
                );
            };

            switch (error?.status) {
                case 409:
                    handleTenantGroupDeleted();

                    break;
                case 404:
                    handleNotFound();
                    break;
                default:
                    handleDefault();
                    break;
            }

            onError?.(error, variables, context);
        },
        [
            onError,
            queryInvalidateFunction,
            addTechnicalErrorModal,
            t,
            clearAll,
            tenantGroupName,
            content,
            history,
            tenantGroupId,
            ruleId
        ]
    );

    return errorHandler;
};

export default useDeleteTenantGroupOverrideErrorHandler;
